import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  Building2,
  Wrench,
  Truck,
  ArrowLeft,
  Play,
  MessageCircle,
  Bell,
  CheckCircle2,
  Smartphone,
  Bot,
  Zap,
  Clock,
  DollarSign,
  MapPin,
  FileText,
  Send,
  AlertTriangle,
  Users,
  Package,
  Shield,
  ChevronRight,
  ExternalLink,
  Copy,
  Check,
  Trash2
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

// AskIsaac Logo
const ASKISAAC_LOGO = "/logos/askisaac-b-monogram.svg";

// Role-specific guide content
const GUIDE_CONTENT = {
  hospital: {
    title: "Hospital Quick-Start Guide",
    subtitle: "Your Telegram & Web Chat Integration Manual",
    icon: Building2,
    color: "from-blue-600 to-cyan-500",
    bgColor: "bg-blue-50",
    description: "Submit maintenance requests, order supplies, run demos, and track your tickets — via Telegram or Web Chat.",
    commands: [
      { cmd: "/start", desc: "Restart onboarding and view demo video", icon: Play },
      { cmd: "/demo", desc: "Run full lifecycle demo simulations", icon: Play },
      { cmd: "/maintenance", desc: "Submit a new maintenance request", icon: Wrench },
      { cmd: "/backorder", desc: "Request supplies or equipment", icon: Package },
      { cmd: "/status", desc: "Check status of your open tickets", icon: Clock },
      { cmd: "/collaborators", desc: "View tagged stakeholders on your tickets", icon: Users },
      { cmd: "/guide", desc: "Open this interaction manual", icon: FileText },
      { cmd: "/support", desc: "Direct line to Isaac Admin", icon: MessageCircle }
    ],
    steps: [
      {
        title: "1. Link Your Account",
        description: "Go to your Hospital Dashboard → Settings → Telegram. Click 'Connect Telegram' and follow the link to @askisaac_bot. Or use the Ask Isaac web chat on desktop.",
        icon: Smartphone
      },
      {
        title: "2. Receive Notifications",
        description: "Once connected, you'll receive real-time alerts when: contractors arrive on-site, quotes are submitted, tickets are updated, and jobs are completed.",
        icon: Bell
      },
      {
        title: "3. Submit Requests",
        description: "Use /maintenance to report equipment issues or /backorder to request supplies. The bot will guide you through selecting priority and entering details.",
        icon: Send
      },
      {
        title: "4. Run Demo Simulations",
        description: "Use /demo to see full lifecycle simulations (Maintenance & Backorder journeys). Perfect for training staff or sales presentations.",
        icon: Play
      },
      {
        title: "5. Track Progress",
        description: "Use /status anytime to see your open tickets. You'll also get automatic updates as your requests progress through the pipeline.",
        icon: CheckCircle2
      }
    ],
    tips: [
      "When submitting maintenance requests, be as specific as possible about the location and issue",
      "Use 'STAT' priority only for true emergencies — it triggers immediate dispatch (2-hour response)",
      "You can reply directly to notification messages to add comments to your tickets",
      "Multi-Stakeholder Sync: Ask admin to tag plumbers, nurses, or suppliers on tickets for coordinated response",
      "Use /demo to run full lifecycle demos for training or presentations — great for new staff onboarding"
    ]
  },
  contractor: {
    title: "Contractor Quick-Start Guide",
    subtitle: "Your Telegram Integration Manual",
    icon: Wrench,
    color: "from-emerald-600 to-teal-500",
    bgColor: "bg-emerald-50",
    description: "Accept jobs, update your status, request parts, and complete work — all from your phone.",
    commands: [
      { cmd: "/start", desc: "Restart onboarding and view demo video", icon: Play },
      { cmd: "/jobs", desc: "View available maintenance & backorder requests", icon: FileText },
      { cmd: "/arrived", desc: "Mark arrival on-site (triggers GPS notification)", icon: MapPin },
      { cmd: "/update", desc: "Send work progress update", icon: MessageCircle },
      { cmd: "/parts", desc: "Request additional parts", icon: Package },
      { cmd: "/complete", desc: "Mark job as done", icon: CheckCircle2 },
      { cmd: "/project_status", desc: "View your project tasks (capital projects)", icon: FileText },
      { cmd: "/status", desc: "Check status of your active quotes/jobs", icon: Clock },
      { cmd: "/guide", desc: "Open this interaction manual", icon: FileText },
      { cmd: "/support", desc: "Direct line to Isaac Admin", icon: MessageCircle }
    ],
    steps: [
      {
        title: "1. Link Your Account",
        description: "Go to your Contractor Dashboard → Settings → Telegram. Click 'Connect' and follow the link to @askisaac_bot.",
        icon: Smartphone
      },
      {
        title: "2. Receive Job Alerts",
        description: "New jobs arrive as 'Blind Requests' — you'll see the job details but not the hospital name until you're assigned. Click 'Quote' to submit your bid.",
        icon: Bell
      },
      {
        title: "3. Accept & Arrive",
        description: "When your quote is approved, you'll get a 'Job Start' notification. Use /arrived when you reach the site — this triggers a GPS-verified notification to the hospital and admin.",
        icon: MapPin
      },
      {
        title: "4. Update & Complete",
        description: "Use /update to send progress notes. If you need parts, use /parts. When finished, use /complete to mark the job done and enter your completion notes.",
        icon: CheckCircle2
      }
    ],
    tips: [
      "The faster you respond to job requests, the more likely you'll win the bid",
      "Always use /arrived when on-site — it's required for GPS verification",
      "Take photos of completed work and upload them through the contractor portal",
      "STAT tier jobs pay premium rates but require 2-hour response times",
      "Multi-Stakeholder Sync: You may be tagged alongside nurses or suppliers — check ticket chat for updates"
    ]
  },
  supplier: {
    title: "Supplier Quick-Start Guide",
    subtitle: "Your Telegram Integration Manual",
    icon: Truck,
    color: "from-amber-500 to-orange-500",
    bgColor: "bg-amber-50",
    description: "Receive RFQs, submit quotes, participate in QuickBids, and manage your team — all through Telegram.",
    commands: [
      { cmd: "/start", desc: "Restart onboarding and view demo video", icon: Play },
      { cmd: "/quotes", desc: "View pending RFQs waiting for your quote", icon: DollarSign },
      { cmd: "/quote", desc: "Start the quote submission process", icon: Send },
      { cmd: "/bid", desc: "View & submit competitive QuickBids", icon: Zap },
      { cmd: "/myquotes", desc: "Track status of your submitted quotes", icon: FileText },
      { cmd: "/jobs", desc: "View service jobs (for service suppliers)", icon: Wrench },
      { cmd: "/assign", desc: "Assign job to technician (Supervisors)", icon: Users },
      { cmd: "/status", desc: "Check overall activity summary", icon: Clock },
      { cmd: "/guide", desc: "Open this interaction manual", icon: FileText },
      { cmd: "/support", desc: "Direct line to Isaac Admin", icon: MessageCircle }
    ],
    steps: [
      {
        title: "1. Link Your Account",
        description: "Go to your Supplier Dashboard → Settings → Telegram. Click 'Connect' and follow the link to @askisaac_bot.",
        icon: Smartphone
      },
      {
        title: "2. Receive RFQ & QuickBid Alerts",
        description: "When a hospital needs supplies, you'll receive an instant notification. High-value orders (>$2,500) trigger QuickBids — time-limited competitive bidding sessions.",
        icon: Bell
      },
      {
        title: "3. Submit Quotes or Bids",
        description: "Use /quotes for standard RFQs or /bid for QuickBid sessions. QuickBids have a deadline countdown — submit your best price before time runs out!",
        icon: Send
      },
      {
        title: "4. Track & Fulfill",
        description: "Use /myquotes to track your submitted quotes. When a quote is approved, you'll receive order details and shipping instructions.",
        icon: CheckCircle2
      }
    ],
    tips: [
      "Respond quickly to RFQs and QuickBids — the system rewards fast responders",
      "QuickBids show a countdown timer — submit before the deadline!",
      "Include delivery timeframes in your quote notes",
      "STAT tier orders require 2-hour fulfillment but command premium margins",
      "Chain of Command: Supervisors can assign jobs to Technicians using /assign"
    ]
  },
  courier: {
    title: "Med Courier Quick-Start Guide",
    subtitle: "Your Telegram Integration Manual",
    icon: Truck,
    color: "from-red-600 to-rose-500",
    bgColor: "bg-red-50",
    description: "Accept STAT deliveries, share live location, verify pickups, complete POD, and track your jobs — all from Telegram.",
    commands: [
      { cmd: "/start", desc: "Restart onboarding and view demo video", icon: Play },
      { cmd: "/courier_jobs", desc: "View your assigned and available delivery jobs", icon: Package },
      { cmd: "/courier_accept [ID]", desc: "Accept an available courier job", icon: CheckCircle2 },
      { cmd: "/courier_pickup [ID]", desc: "Mark cargo as picked up", icon: MapPin },
      { cmd: "/courier_complete [ID]", desc: "Start Proof of Delivery (POD) flow", icon: FileText },
      { cmd: "📍 Share Location", desc: "Enable live GPS tracking for admin dashboard", icon: MapPin },
      { cmd: "/status", desc: "Check overall delivery summary", icon: Clock },
      { cmd: "/guide", desc: "Open this interaction manual", icon: FileText },
      { cmd: "/support", desc: "Direct line to Isaac Admin", icon: MessageCircle }
    ],
    steps: [
      {
        title: "1. Get Certified & Registered",
        description: "Complete required certifications (BBP, HIPAA, Specimen Handling) and register as a Med Courier in the contractor portal.",
        icon: Shield
      },
      {
        title: "2. Receive Job Alerts",
        description: "When a STAT delivery matches your certifications and location, you'll receive an instant notification with cargo type, urgency, and pickup/delivery addresses.",
        icon: Bell
      },
      {
        title: "3. Enable Live Tracking",
        description: "Share your live location in Telegram for real-time tracking. Tap 📎 → Location → Share Live Location. This enables the Fleet Command Center and hospital tracking links.",
        icon: MapPin
      },
      {
        title: "4. Accept & Pickup",
        description: "Use /courier_accept to accept a job, then /courier_pickup when you arrive. Share your GPS location for verification.",
        icon: MapPin
      },
      {
        title: "5. Complete with POD",
        description: "At delivery, use /courier_complete to start the Proof of Delivery flow: photo, receiver name, signature. GPS verification within 100 yards is required.",
        icon: CheckCircle2
      }
    ],
    tips: [
      "STAT/Critical jobs pay premium rates but require immediate response",
      "Always share your GPS location for pickup and delivery verification",
      "Complete all POD steps — photo, name, signature — to close a job",
      "Keep your certifications current; expired certs disqualify you from specialized cargo",
      "The Anti-Ghosting system flags incomplete POD attempts to admin"
    ]
  },
  admin: {
    title: "Admin QuickBid & Security Guide",
    subtitle: "Enterprise Management Manual",
    icon: Shield,
    color: "from-purple-600 to-indigo-500",
    bgColor: "bg-purple-50",
    description: "Manage procurement, security settings, demo simulations, compliance, and vendor showcases.",
    commands: [
      { cmd: "/demo", desc: "Run full lifecycle demo simulations for presentations", icon: Play },
      { cmd: "/quickbid", desc: "View active QuickBid sessions", icon: DollarSign },
      { cmd: "/dispatch [ID]", desc: "Dispatch a courier job to specific driver", icon: Truck },
      { cmd: "/force_close [ID]", desc: "Admin override for stuck jobs (bypasses geo-fence)", icon: AlertTriangle },
      { cmd: "/reset_demo_vault", desc: "Clear demo data after presentations (SuperAdmin)", icon: Trash2 },
      { cmd: "/compliance", desc: "Generate compliance report for auditors", icon: FileText },
      { cmd: "/vendors", desc: "View published vendor showcases", icon: Building2 },
      { cmd: "/admin", desc: "Access full admin command menu", icon: Shield }
    ],
    steps: [
      {
        title: "1. Configure Security Suite",
        description: "Enable Zero-Knowledge security, MFA requirements, session timeouts, and PHI detection via Admin Dashboard → Security Settings.",
        icon: Shield
      },
      {
        title: "2. Configure Thresholds",
        description: "Set competitive bidding thresholds per health system (default $2,500 for Texas compliance). Access via /quickbid or Admin Dashboard.",
        icon: DollarSign
      },
      {
        title: "3. Run Demo Simulations",
        description: "Use /demo to run full lifecycle demos (Maintenance & Backorder) for sales presentations. Use /reset_demo_vault to clear demo data afterward.",
        icon: Play
      },
      {
        title: "4. Auto-Triggered Bidding",
        description: "When a request exceeds the threshold, QuickBid automatically opens a 60-minute blind bid window and notifies eligible suppliers via Telegram.",
        icon: Bell
      },
      {
        title: "5. Review & Award",
        description: "Compare top 3 bids, view supplier profiles in Vendor Showcase, and award to the best offer. System generates audit trail automatically.",
        icon: CheckCircle2
      },
      {
        title: "6. Compliance Records",
        description: "All high-value procurements are documented with full bid history, award rationale, and compliance statements for auditors.",
        icon: FileText
      }
    ],
    tips: [
      "Use /demo for sales presentations — shows full 6-step lifecycle simulations with compliance hashes",
      "QuickBid ensures Texas hospital procurement compliance for purchases >$2,500",
      "Suppliers with Vendor Showcase profiles display verified certifications and capability statements",
      "Use /force_close sparingly — it bypasses geo-fence verification but logs admin override",
      "Compliance records are exportable for external audits via Security Whitepaper page",
      "/reset_demo_vault clears simulation data — use after presentations to keep dashboard clean"
    ]
  }
};

// Copy button component
const CopyButton = ({ text }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    toast.success("Copied to clipboard!");
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <button
      onClick={handleCopy}
      className="p-1 hover:bg-slate-200 rounded transition-colors"
      title="Copy command"
    >
      {copied ? (
        <Check className="w-4 h-4 text-emerald-600" />
      ) : (
        <Copy className="w-4 h-4 text-slate-400" />
      )}
    </button>
  );
};

export default function UserGuidesPage() {
  const { role } = useParams();
  const [activeTab, setActiveTab] = useState(role || "hospital");
  
  // If role is specified in URL, use it
  const currentRole = role || activeTab;
  const guide = GUIDE_CONTENT[currentRole] || GUIDE_CONTENT.hospital;
  const Icon = guide.icon;

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900 text-white">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-slate-900/90 backdrop-blur-xl border-b border-slate-700/50">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3">
              <img src={ASKISAAC_LOGO} alt="AskIsaac" className="w-10 h-10 rounded-xl" />
              <div>
                <h1 className="font-bold text-lg tracking-tight">AskIsaac</h1>
                <p className="text-xs text-slate-400">Telegram Guide</p>
              </div>
            </Link>
            <Link to="/">
              <Button variant="outline" size="sm" className="border-slate-600 text-slate-300 hover:bg-slate-800">
                <ArrowLeft className="w-4 h-4 mr-2" /> Back
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="py-12 px-4">
        <div className="max-w-6xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-cyan-500/10 border border-cyan-500/30 rounded-full text-cyan-400 text-sm mb-6">
            <Bot className="w-4 h-4" />
            @askisaac_bot
          </div>
          <h1 className="text-4xl lg:text-5xl font-bold mb-4">
            Telegram Quick-Start Guide
          </h1>
          <p className="text-xl text-slate-400 max-w-2xl mx-auto">
            Your complete guide to using AskIsaac through Telegram. Select your role below.
          </p>
        </div>
      </section>

      {/* Role Tabs */}
      {!role && (
        <section className="px-4 mb-8">
          <div className="max-w-4xl mx-auto">
            <div className="flex justify-center gap-3 flex-wrap">
              {Object.entries(GUIDE_CONTENT).map(([key, content]) => {
                const TabIcon = content.icon;
                return (
                  <button
                    key={key}
                    onClick={() => setActiveTab(key)}
                    className={`flex items-center gap-2 px-6 py-3 rounded-xl font-medium transition-all ${
                      activeTab === key
                        ? `bg-gradient-to-r ${content.color} text-white shadow-lg`
                        : "bg-slate-800/50 text-slate-400 hover:text-white hover:bg-slate-700/50"
                    }`}
                    data-testid={`guide-tab-${key}`}
                  >
                    <TabIcon className="w-5 h-5" />
                    {key.charAt(0).toUpperCase() + key.slice(1)}
                  </button>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Main Content */}
      <section className="px-4 pb-16">
        <div className="max-w-4xl mx-auto">
          {/* Role Header */}
          <div className={`bg-gradient-to-r ${guide.color} rounded-2xl p-8 mb-8`}>
            <div className="flex items-center gap-4 mb-4">
              <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center">
                <Icon className="w-8 h-8 text-white" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-white">{guide.title}</h2>
                <p className="text-white/80">{guide.subtitle}</p>
              </div>
            </div>
            <p className="text-white/90 text-lg">{guide.description}</p>
          </div>

          {/* Demo Video Section */}
          <div className="bg-slate-800/50 border border-slate-700/50 rounded-2xl p-6 mb-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-red-500/20 rounded-xl flex items-center justify-center">
                <Play className="w-5 h-5 text-red-400" />
              </div>
              <div>
                <h3 className="font-bold text-lg">Interactive Walkthrough</h3>
                <p className="text-slate-400 text-sm">Step-by-step guide to using @askisaac_bot</p>
              </div>
            </div>
            <div className="bg-slate-900 rounded-xl aspect-video flex items-center justify-center border border-slate-700">
              <div className="text-center">
                <Link 
                  to="/telegram-walkthrough" 
                  className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-red-500 to-pink-500 hover:from-red-600 hover:to-pink-600 rounded-xl font-semibold transition-all"
                  data-testid="watch-walkthrough-btn"
                >
                  <Play className="w-5 h-5" />
                  Start Interactive Walkthrough
                </Link>
                <p className="text-slate-500 text-sm mt-3">2-minute guided tour</p>
              </div>
            </div>
          </div>

          {/* Commands Reference */}
          <div className="bg-slate-800/50 border border-slate-700/50 rounded-2xl p-6 mb-8">
            <h3 className="font-bold text-xl mb-4 flex items-center gap-2">
              <MessageCircle className="w-5 h-5 text-cyan-400" />
              Your Commands
            </h3>
            <div className="grid gap-3">
              {guide.commands.map((command, idx) => {
                const CmdIcon = command.icon;
                return (
                  <div key={idx} className="flex items-center gap-4 p-3 bg-slate-900/50 rounded-xl border border-slate-700/50">
                    <div className="w-10 h-10 bg-slate-800 rounded-lg flex items-center justify-center flex-shrink-0">
                      <CmdIcon className="w-5 h-5 text-cyan-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <code className="text-cyan-400 font-mono font-bold">{command.cmd}</code>
                        <CopyButton text={command.cmd} />
                      </div>
                      <p className="text-slate-400 text-sm">{command.desc}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Getting Started Steps */}
          <div className="bg-slate-800/50 border border-slate-700/50 rounded-2xl p-6 mb-8">
            <h3 className="font-bold text-xl mb-6 flex items-center gap-2">
              <Zap className="w-5 h-5 text-amber-400" />
              Getting Started
            </h3>
            <div className="space-y-6">
              {guide.steps.map((step, idx) => {
                const StepIcon = step.icon;
                return (
                  <div key={idx} className="flex gap-4">
                    <div className="flex-shrink-0">
                      <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${guide.color} flex items-center justify-center`}>
                        <StepIcon className="w-6 h-6 text-white" />
                      </div>
                    </div>
                    <div>
                      <h4 className="font-semibold text-lg mb-1">{step.title}</h4>
                      <p className="text-slate-400">{step.description}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Pro Tips */}
          <div className="bg-gradient-to-r from-amber-500/10 to-orange-500/10 border border-amber-500/30 rounded-2xl p-6 mb-8">
            <h3 className="font-bold text-xl mb-4 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-400" />
              Pro Tips
            </h3>
            <ul className="space-y-3">
              {guide.tips.map((tip, idx) => (
                <li key={idx} className="flex items-start gap-3">
                  <ChevronRight className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                  <span className="text-slate-300">{tip}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Connect CTA */}
          <div className="bg-gradient-to-r from-cyan-600/20 to-blue-600/20 border border-cyan-500/30 rounded-2xl p-8 text-center">
            <h3 className="text-2xl font-bold mb-3">Ready to Connect?</h3>
            <p className="text-slate-400 mb-6 max-w-md mx-auto">
              Open Telegram and search for <span className="text-cyan-400 font-semibold">@askisaac_bot</span> to get started.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <a 
                href="https://t.me/askisaac_bot"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-cyan-600 to-blue-500 hover:from-cyan-700 hover:to-blue-600 rounded-xl font-semibold transition-all"
                data-testid="connect-telegram-btn"
              >
                <Bot className="w-5 h-5" />
                Open @askisaac_bot
                <ExternalLink className="w-4 h-4" />
              </a>
              <Link to="/">
                <Button variant="outline" className="border-slate-600 text-slate-300 hover:bg-slate-800">
                  <ArrowLeft className="w-4 h-4 mr-2" /> Back to Home
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-slate-700/50 py-8">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <p className="text-slate-500 text-sm">
            Need help? Contact support at <a href="mailto:info@sterilemate.com" className="text-cyan-400 hover:underline">info@sterilemate.com</a> or call 800.988.3892
          </p>
        </div>
      </footer>
    </div>
  );
}
